/**
 * useApi Hook
 * React hook for using the API client in components
 */

import {useCallback, useEffect, useState} from 'react';
import {ApiClient} from '../ApiClient';
import {ApiError, ApiResponse} from '../types';
import {LoadingStateManager} from '@/services/api';

/**
 * Hook result interface
 */
interface UseApiResult<T> {
  // Data returned from the API
  data: T | null;
  // Error from the API call
  error: ApiError | null;
  // Loading state
  loading: boolean;
  // Function to execute the API call
  execute: () => Promise<ApiResponse<T>>;
  // Function to reset the hook state
  reset: () => void;
}

/**
 * Hook for making API calls with automatic loading state management
 * @param apiClient - The API client instance
 * @param apiCall - Function that returns a promise from the API client
 * @param loadingStateManager - Optional loading state manager
 * @param loadingKey - Optional key for the loading state
 * @param executeImmediately - Whether to execute the API call immediately
 * @param retryOnError - Whether to retry the API call after an error (default: true)
 * @returns Hook result with data, error, loading state, and execute function
 * 
 * Note: If a request completes successfully, it will not be repeated automatically.
 * Use the execute function to manually trigger a new request if needed.
 */
export function useApi<T>(
  apiClient: ApiClient,
  apiCall: (client: ApiClient) => Promise<ApiResponse<T>>,
  loadingStateManager?: LoadingStateManager,
  loadingKey?: string,
  executeImmediately = true,
  retryOnError = false
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(false);
  // Track if there was an error
  const [hadError, setHadError] = useState(false);
  // Track if request was successful
  const [wasSuccessful, setWasSuccessful] = useState(false);

  // Function to execute the API call
  const execute = useCallback(async (): Promise<ApiResponse<T>> => {
    try {
      // Set loading state
      setLoading(true);
      if (loadingStateManager && loadingKey) {
        loadingStateManager.setLoading(loadingKey, true);
      }

      // Reset previous state
      setError(null);

      // Execute the API call
      const response = await apiCall(apiClient);

      // Set data
      setData(response.data);

      // Mark request as successful
      setWasSuccessful(true);

      return response;
    } catch (err) {
      // Set error
      const apiError = err as ApiError;
      setError(apiError);
      throw apiError;
    } finally {
      // Clear loading state
      setLoading(false);
      if (loadingStateManager && loadingKey) {
        loadingStateManager.setLoading(loadingKey, false);
      }
    }
  }, [apiClient, apiCall, loadingStateManager, loadingKey]);

  // Function to reset the hook state
  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
    setHadError(false);
    setWasSuccessful(false);
  }, []);

  // Update hadError when error changes
  useEffect(() => {
    if (error) {
      setHadError(true);
    }
  }, [error]);

  // Execute the API call immediately if requested
  useEffect(() => {
    if (executeImmediately && !wasSuccessful && (!hadError || retryOnError)) {
      execute().catch(() => {
        // Error is already handled in execute function
      });
    }
  }, [execute, executeImmediately, hadError, retryOnError, wasSuccessful]);

  return { data, error, loading, execute, reset };
}

/**
 * Hook for making API calls with automatic loading state management and data transformation
 * @param apiClient - The API client instance
 * @param apiCall - Function that returns a promise from the API client
 * @param transform - Function to transform the API response data
 * @param loadingStateManager - Optional loading state manager
 * @param loadingKey - Optional key for the loading state
 * @param executeImmediately - Whether to execute the API call immediately
 * @param retryOnError - Whether to retry the API call after an error (default: true)
 * @returns Hook result with transformed data, error, loading state, and execute function
 * 
 * Note: If a request completes successfully, it will not be repeated automatically.
 * Use the execute function to manually trigger a new request if needed.
 */
export function useApiWithTransform<T, R>(
  apiClient: ApiClient,
  apiCall: (client: ApiClient) => Promise<ApiResponse<T>>,
  transform: (data: T) => R,
  loadingStateManager?: LoadingStateManager,
  loadingKey?: string,
  executeImmediately = false,
  retryOnError = true
): Omit<UseApiResult<T>, 'data'> & { data: R | null } {
  const { data: originalData, ...rest } = useApi<T>(
    apiClient,
    apiCall,
    loadingStateManager,
    loadingKey,
    executeImmediately,
    retryOnError
  );

  // Transform the data
  const transformedData = originalData ? transform(originalData) : null;

  return {
    ...rest,
    data: transformedData,
  };
}
